import { useCallback, useState, type FocusEventHandler } from 'react';
import { cn } from '@/shared/utils/cn';

type FloatingFocusElement = HTMLInputElement | HTMLSelectElement;

export function useFloatingLabelState(isFilled: boolean) {
  const [isFocused, setIsFocused] = useState(false);
  const isLabelActive = isFocused || isFilled;

  const mergeFocusHandlers = useCallback(
    (
      onFocus?: FocusEventHandler<FloatingFocusElement>,
      onBlur?: FocusEventHandler<FloatingFocusElement>
    ) => ({
      onFocus: (event: React.FocusEvent<FloatingFocusElement>) => {
        setIsFocused(true);
        onFocus?.(event);
      },
      onBlur: (event: React.FocusEvent<FloatingFocusElement>) => {
        setIsFocused(false);
        onBlur?.(event);
      },
    }),
    []
  );

  return { isLabelActive, isFocused, mergeFocusHandlers };
}

export function getFloatingLabelClass(isActive: boolean) {
  return cn(
    'pointer-events-none absolute left-[11px] origin-left text-[#808080] transition-all duration-200 ease-in-out',
    isActive
      ? 'top-[7px] translate-y-0 scale-[0.85] text-[13px] leading-tight'
      : 'top-1/2 -translate-y-1/2 text-base font-normal leading-6 text-[#808080]'
  );
}

export function getFloatingFieldWrapperClass(
  isFilled: boolean,
  disabled: boolean,
  hasError: boolean,
  isFocused: boolean
) {
  return cn(
    'relative min-h-[48px] border-2 transition-colors duration-200',
    getFloatingFieldBorderClass(isFilled, disabled, hasError),
    isFocused && !hasError && !isFilled && !disabled && 'border-[#4476b0]'
  );
}

export function getFloatingFieldBorderClass(isFilled: boolean, disabled: boolean, hasError: boolean) {
  if (hasError) return 'border-red-500 bg-white';
  if (disabled) return 'border-[#b7b7b7] bg-[#e2e2e2]';
  if (isFilled) return 'border-[#59ac59] bg-white';
  return 'border-[#e2e2e2] bg-white';
}
